import { ImagesType } from "@features/images/type";
import {
  getImageSizeFromSteps,
  getImageUrlFromSteps,
} from "@features/images/utils";
import {
  AdjustmentSettings,
  createKonvaFilters,
} from "@views/app/studio/components/actions/effects/cursors";
import { filters } from "@views/app/studio/components/actions/effects/filters";
import Konva from "konva";
import { applyChipOverlayToCanvas } from "./render-chip-overlay";
import { StudioStateType } from "./use-studio";

/**
 * Apply effects to a canvas using Konva's offline rendering
 */
const applyEffectsToCanvas = async (
  sourceCanvas: HTMLCanvasElement,
  filterName: string | null,
  cursorSettings: Partial<AdjustmentSettings>
): Promise<HTMLCanvasElement> => {
  // Create an offline Konva stage
  const stage = new Konva.Stage({
    container: document.createElement("div"),
    width: sourceCanvas.width,
    height: sourceCanvas.height,
  });

  const layer = new Konva.Layer();
  stage.add(layer);

  // Create an image element from the canvas first
  const imageElement = new Image();
  imageElement.src = sourceCanvas.toDataURL();

  await new Promise((resolve) => {
    imageElement.onload = resolve;
  });

  // Create a Konva image from the image element
  const konvaImage = new Konva.Image({
    x: 0,
    y: 0,
    width: sourceCanvas.width,
    height: sourceCanvas.height,
    image: imageElement,
  });

  layer.add(konvaImage);

  // Create a mock ref for the image to use with createKonvaFilters
  const mockImageRef = { current: konvaImage };

  // Build and apply filters
  const filter = filterName ? (filters as any)[filterName] : null;
  const cursorFilters = createKonvaFilters(mockImageRef, cursorSettings);
  const combinedFilters: any[] = [];

  if (filter) combinedFilters.push(filter as any);
  combinedFilters.push(...cursorFilters);

  if (combinedFilters.length > 0) {
    konvaImage.filters(combinedFilters);
    konvaImage.cache();
  }

  layer.batchDraw();

  // Convert the stage to canvas
  const resultCanvas = stage.toCanvas();

  // Clean up
  stage.destroy();

  return resultCanvas;
};

export const renderImage = async (
  image: ImagesType,
  state: StudioStateType,
  options: {
    format: "jpg" | "png";
    transform?: boolean;
    drawing?: boolean;
    effects?: boolean;
    frame?: boolean;
    chips?: boolean;
    ignore_edited_image?: boolean;
  } = { format: "jpg" }
): Promise<string> => {
  // Create a canvas to render the current view with empty zones as black
  const canvas = document.createElement("canvas");
  canvas.width = options?.frame ? state.frame.width : image.size.width;
  canvas.height = options?.frame ? state.frame.height : image.size.height;
  const ctx = canvas.getContext("2d");

  if (!ctx) {
    throw new Error("Failed to get canvas context");
  }

  // Fill with black background (empty zones)
  if (options?.format === "jpg") {
    ctx.fillStyle = "black";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }

  // Load and place the original image
  const img = new Image();
  img.crossOrigin = "anonymous";

  await new Promise((resolve, reject) => {
    img.onload = resolve;
    img.onerror = reject;
    img.src = getImageUrlFromSteps(
      image?.steps.filter(
        (a) => a.mode !== "edits" || !options?.ignore_edited_image
      )
    );
    console.log("Rendering image from URL:", img.src);
  });

  // Place the image with the current transform
  placeImageOnCanvas(
    img,
    canvas,
    options?.transform
      ? state.transform
      : {
          x: 0,
          y: 0,
          scaleX: 1,
          scaleY: 1,
          rotation: 0,
        }
  );

  if (options?.drawing && state.drawing_svg_ref?.current) {
    // Apply the drawing mask if any
    await applyMaskFromSVG(ctx, state.drawing_svg_ref.current);
  }

  // Apply chip overlay markers if any
  if (options?.chips && state.chips && state.chips.length > 0) {
    applyChipOverlayToCanvas(ctx, state.chips);
  }

  // Apply effects if any are set
  let finalCanvas = canvas;
  const hasEffects =
    state.effects.filter ||
    Object.values(state.effects.adjustements || {}).some(
      (value) => value !== 0
    );

  if (hasEffects && options?.effects) {
    finalCanvas = await applyEffectsToCanvas(
      canvas,
      state.effects.filter,
      state.effects.adjustements
    );
  }

  // Convert to base64
  const imageData =
    options?.format === "jpg"
      ? finalCanvas.toDataURL("image/jpeg", 1)
      : finalCanvas.toDataURL("image/png");

  // Clean up
  canvas.remove();
  if (finalCanvas !== canvas) {
    finalCanvas.remove();
  }

  return imageData;
};

export const placeImageOnCanvas = (
  image: HTMLImageElement,
  canvas: HTMLCanvasElement,
  transform: {
    x: number;
    y: number;
    scaleX: number;
    scaleY: number;
    rotation: number;
  }
) => {
  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  // Clear the canvas
  ctx.clearRect(0, 0, canvas.width, canvas.height);

  // Save the current context state
  ctx.save();

  // Apply transformations
  ctx.translate(
    transform.x + canvas.width / 2,
    transform.y + canvas.height / 2
  );
  ctx.rotate((transform.rotation * Math.PI) / 180);
  ctx.scale(transform.scaleX, transform.scaleY);

  // Draw the image centered at the origin
  ctx.drawImage(image, -image.width / 2, -image.height / 2);

  // Restore the context to its original state
  ctx.restore();
};

export const applyMaskFromSVG = async (
  ctx: CanvasRenderingContext2D,
  svgElement: SVGSVGElement,
  asBlack = false
) => {
  if (!ctx || !svgElement) return;

  try {
    const svgData = new XMLSerializer().serializeToString(svgElement);
    const svgBlob = new Blob([svgData], {
      type: "image/svg+xml;charset=utf-8",
    });
    const url = URL.createObjectURL(svgBlob);

    const img = new Image();
    await new Promise((resolve, reject) => {
      img.onload = () => {
        // Draw the SVG image onto the canvas, cut out the non black areas
        if (!asBlack) {
          const temp = document.createElement("canvas");
          temp.width = ctx.canvas.width;
          temp.height = ctx.canvas.height;
          const tctx = temp.getContext("2d");

          // draw mask
          tctx!.drawImage(img, 0, 0, temp.width, temp.height);

          // convert white → transparent, black → opaque
          const imgData = tctx!.getImageData(0, 0, temp.width, temp.height);
          const data = imgData.data;
          for (let i = 0; i < data.length; i += 4) {
            // compute brightness
            const brightness = (data[i] + data[i + 1] + data[i + 2]) / 3;
            data[i + 3] = brightness;
            // white (255) → alpha 0, black (0) → alpha 255
          }
          tctx!.putImageData(imgData, 0, 0);

          ctx.globalCompositeOperation = "destination-out";
          ctx.drawImage(temp, 0, 0, ctx.canvas.width, ctx.canvas.height);
        } else {
          // Revert colors of img
          ctx.drawImage(img, 0, 0, ctx.canvas.width, ctx.canvas.height);
        }
        URL.revokeObjectURL(url);
        resolve(null);
      };
      img.src = url;
      img.onerror = (e) => {
        console.error("Failed to load SVG image for mask:", e);
        URL.revokeObjectURL(url);
        reject(e);
      };
    });
  } catch (e) {
    console.error("Error applying mask from SVG:", e);
    return;
  }
};

export const isThereEmptyZones = async (
  image: ImagesType,
  frame: { width: number; height: number },
  transform?: {
    x: number;
    y: number;
    scaleX: number;
    scaleY: number;
    rotation: number;
  },
  drawing?: SVGSVGElement
): Promise<boolean> => {
  if (!image || !frame || (!transform && !drawing)) return false;

  const canvas = document.createElement("canvas");
  canvas.width = frame.width || image.size.width;
  canvas.height = frame.height || image.size.height;

  const ctx = canvas.getContext("2d");
  if (!ctx) return false;

  const lastStepSize = getImageSizeFromSteps(
    image?.steps.filter((a) => a.mode !== "edits")
  );

  // Create the image element (black image, with image width / height)
  const img = document.createElement("canvas");
  img.width = lastStepSize.width;
  img.height = lastStepSize.height;
  const imgCtx = img.getContext("2d");
  if (!imgCtx) return false;
  imgCtx.fillStyle = "white";
  imgCtx.fillRect(0, 0, img.width, img.height);
  const imageElement = new Image();
  imageElement.src = imgCtx.canvas.toDataURL();

  await new Promise((resolve, reject) => {
    imageElement.onload = resolve;
    imageElement.onerror = reject;
  });

  placeImageOnCanvas(
    imageElement,
    canvas,
    transform || {
      x: 0,
      y: 0,
      scaleX: 1,
      scaleY: 1,
      rotation: 0,
    }
  );

  if (drawing) {
    await applyMaskFromSVG(ctx, drawing);
  }

  // Debug: show on page
  const debug = document.location.search.indexOf("debug") !== -1;
  if (debug) {
    console.log("Empty zones canvas debug", canvas);
    document.querySelectorAll("#debug-canvas").forEach((a) => a.remove());
    canvas.id = "debug-canvas";
    canvas.style.position = "fixed";
    canvas.style.top = "10px";
    canvas.style.bottom = "10px";
    canvas.style.width = "200px";
    canvas.style.border = "1px solid black";
    canvas.style.zIndex = "9999";
    canvas.style.pointerEvents = "none";
    canvas.style.opacity = "0.7";
    document.body.appendChild(canvas);
  }

  const isThereAlpha = ctx
    .getImageData(0, 0, canvas.width, canvas.height)
    .data.some((value) => value < 255);

  // Clean up the canvas element
  if (!debug) canvas.remove();
  img.remove();

  if (!isThereAlpha) return false;
  return true;
};
